import { Box, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

export type StatusTone = 'neutral' | 'success' | 'warning' | 'error';

type Props = {
  tone?: StatusTone;
  icon: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  /** Tlačidlá pod textom. Prvé je primárne - obrazovka má práve jednu cestu ďalej. */
  actions?: React.ReactNode;
  /** Technický kód pre nahlásenie, napr. `VOTING_CLOSED · 30. 9. 2026 20:02`. */
  code?: string;
  children?: React.ReactNode;
};

const circle = (tone: StatusTone) => {
  switch (tone) {
    case 'success':
      return { bg: 'green.main', fg: 'common.white', border: 'transparent' };
    case 'error':
      return {
        bg: 'primary.main',
        fg: 'primary.contrastText',
        border: 'transparent',
      };
    case 'warning':
      return {
        bg: 'yellow.surface',
        fg: 'yellow.surfaceText',
        border: 'yellow.surfaceBorder',
      };
    default:
      return { bg: 'grey.100', fg: 'grey.600', border: 'transparent' };
  }
};

/**
 * Obrazovka s jednou akciou - „hlasovanie sa ešte nezačalo", „je uzavreté",
 * „hlas už prišiel", „odoslanie zlyhalo", „váš hlas je odovzdaný".
 *
 * Nahrádza `ResultOverlay`, ktorý mal na všetkých päť situácií jedinú vetu
 * a jediné tlačidlo „Späť na Hlasovania". Prekrytie navyše stálo nad
 * formulárom, ktorý zostal pod ním viditeľný, takže vyzeralo, že sa dá
 * pokračovať.
 */
const VotingStatusScreen: React.FC<Props> = ({
  tone = 'neutral',
  icon,
  title,
  description,
  actions,
  code,
  children,
}) => {
  const colors = circle(tone);

  return (
    <Stack
      alignItems='center'
      justifyContent='center'
      sx={{ flex: 1, minHeight: 0, p: { xs: 3, md: 4 } }}
    >
      <Stack
        alignItems='center'
        gap={2}
        sx={{ maxWidth: 480, width: '100%', textAlign: 'center' }}
      >
        <Box
          sx={({ palette, shape }) => ({
            width: 56,
            height: 56,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${shape.borderRadiusFull}px`,
            backgroundColor: colors.bg,
            color: colors.fg,
            border:
              colors.border === 'transparent'
                ? 'none'
                : `1px solid ${palette.yellow.surfaceBorder}`,
          })}
        >
          {icon}
        </Box>

        <Typography
          component='h2'
          sx={{
            fontSize: { xs: 21, md: 24 },
            fontWeight: 700,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ lineHeight: 1.6 }}
          >
            {description}
          </Typography>
        )}

        {children}

        {actions && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            sx={{ mt: 0.5, width: '100%', justifyContent: 'center' }}
          >
            {actions}
          </Stack>
        )}

        {code && (
          <Typography variant='caption' color='text.secondary'>
            {code}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default VotingStatusScreen;
